@if (loading()) {
  <div class="rounded-2xl border border-secondary-border bg-white p-8">
    <p>{{ 'pages.articlePurchase.loading' | translate }}</p>
  </div>
} @else if (loadErrorKey()) {
  <div class="rounded-2xl border border-secondary-border bg-white p-8">
    <div class="flex flex-col gap-4">
      <h1 class="text-3xl font-medium text-primary-text">
        {{ 'pages.articlePurchase.title' | translate }}
      </h1>
      <p class="text-base text-error-text">
        {{ loadErrorKey()! | translate }}
      </p>
      <div>
        <a
          class="btn-secondary btn"
          [routerLink]="article() ? ['/articles', article()!.id] : ['/']"
        >
          {{ 'pages.articlePurchase.backToArticle' | translate }}
        </a>
      </div>
    </div>
  </div>
} @else if (article(); as article) {
  <form
    class="flex flex-col-reverse gap-8 md:flex-row"
    [formGroup]="customerFormGroup"
    (ngSubmit)="submit()"
  >
    <div class="flex flex-grow flex-col gap-4 md:w-1/2 lg:w-3/5">
      <div
        class="flex flex-col gap-8 rounded-2xl border border-secondary-border bg-white p-8"
      >
        <div class="flex flex-col gap-3 border-b border-secondary-border pb-6">
          <p
            class="text-sm font-medium tracking-wide text-secondary-text uppercase"
          >
            {{ 'pages.articlePurchase.title' | translate }}
          </p>
          <h1 class="text-3xl leading-tight font-medium text-primary-text">
            {{ article.title }}
          </h1>
          <p class="text-base text-secondary-text">
            {{ 'pages.articlePurchase.description' | translate }}
          </p>
          <p class="text-sm text-secondary-text">
            {{ 'pages.articlePurchase.buyingAsIndividual' | translate }}
          </p>
        </div>

        @if (isManagedUser()) {
          <div
            class="rounded-xl border border-primary-border bg-ocean-blue-50 p-4"
          >
            <p class="text-sm text-primary-text">
              {{ 'pages.articlePurchase.managedUserNotice' | translate }}
            </p>
          </div>
        }

        <div class="flex flex-col gap-4">
          <h2 class="text-xl font-medium text-primary-text">
            {{ 'pages.articlePurchase.customerDetailsTitle' | translate }}
          </h2>
          <div class="grid gap-4 md:grid-cols-2">
            @for (field of this.customerFields; track field.formControlName) {
              <dhp-input-field [field]="field"></dhp-input-field>
            }
          </div>
        </div>

        @if (requiresAddress()) {
          <div class="flex flex-col gap-4">
            <h2 class="text-xl font-medium text-primary-text">
              {{ 'pages.articlePurchase.addressDetailsTitle' | translate }}
            </h2>

            <div class="grid gap-4 md:grid-cols-2">
              <dhp-input-field [field]="countryAddressField"></dhp-input-field>
              <dhp-input-field
                [field]="provinceAddressField()"
              ></dhp-input-field>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <dhp-input-field [field]="cityAddressField"></dhp-input-field>
              <dhp-input-field [field]="zipCodeAddressField"></dhp-input-field>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <dhp-input-field [field]="streetAddressField"></dhp-input-field>
              <div class="grid gap-4 md:grid-cols-2">
                <dhp-input-field
                  [field]="houseNumberAddressField"
                ></dhp-input-field>
                <dhp-input-field
                  [field]="additionAddressField"
                ></dhp-input-field>
              </div>
            </div>
          </div>
        }

        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            class="btn-secondary btn sm:w-fit"
            [routerLink]="['/articles', article.id]"
          >
            {{ 'pages.articlePurchase.backToArticle' | translate }}
          </a>
        </div>
      </div>
    </div>

    <aside
      class="flex w-full flex-col gap-4 rounded-2xl border border-secondary-border bg-white p-8 md:w-1/2 lg:w-2/5"
      [formGroup]="confirmationFormGroup"
    >
      <div class="border-b border-secondary-border pb-4">
        <h2 class="text-2xl font-medium text-primary-text">
          {{ 'pages.articlePurchase.summaryTitle' | translate }}
        </h2>
      </div>

      <div class="flex flex-col gap-1">
        <p class="text-base font-medium text-primary-text">
          {{ article.title }}
        </p>
        <p class="text-sm text-secondary-text">
          {{ article.magazine.title }}
        </p>
      </div>

      <div class="flex flex-col gap-4 text-sm text-primary-text">
        @if (studentPricingApplies()) {
          <div class="flex flex-col gap-3">
            <span class="text-sm font-medium text-primary-text">{{
              'pages.articlePurchase.pricingOptionsTitle' | translate
            }}</span>

            <label
              class="flex cursor-pointer items-start gap-3 rounded-xl border border-secondary-border p-4"
              [ngClass]="{
                'border-primary-border bg-primary-background':
                  selectedPricingType() === 'regular',
              }"
            >
              <input
                class="radio mt-1"
                formControlName="pricingType"
                type="radio"
                value="regular"
              />
              <span class="flex flex-1 flex-col gap-1">
                <span class="font-medium text-primary-text">
                  {{ 'pages.articlePurchase.regularPriceOption' | translate }}
                </span>
                <span class="text-secondary-text">
                  {{ purchaseTotal() | currency: 'EUR' }}
                </span>
              </span>
            </label>

            <label
              class="flex cursor-pointer items-start gap-3 rounded-xl border border-secondary-border p-4"
              [ngClass]="{
                'border-primary-border bg-primary-background':
                  selectedPricingType() === 'student',
              }"
            >
              <input
                class="radio mt-1"
                formControlName="pricingType"
                type="radio"
                value="student"
              />
              <span class="flex flex-1 flex-col gap-1">
                <span class="font-medium text-primary-text">
                  {{ 'pages.articlePurchase.studentPriceOption' | translate }}
                </span>
                <span class="text-secondary-text">
                  {{ studentPurchaseTotal() | currency: 'EUR' }}
                </span>
                @if (showStudentVerificationMessage()) {
                  <span class="text-sm font-normal text-primary-text">
                    {{
                      'pages.articlePurchase.studentVerificationRequired'
                        | translate
                    }}
                  </span>
                }
              </span>
            </label>
          </div>
        }

        <div class="flex items-start justify-between gap-4">
          <span>{{ 'pages.articlePurchase.summarySubtotal' | translate }}</span>
          <div class="flex flex-col items-end gap-1 text-right">
            @if (useStudentPricing()) {
              <p>
                <span class="text-secondary-text line-through">
                  {{ purchaseSubtotal() | currency: 'EUR' }}
                </span>
                <span class="font-medium text-primary-text">
                  {{ effectivePurchaseSubtotal() | currency: 'EUR' }}
                </span>
              </p>

              <span class="text-xs text-secondary-text">
                {{ 'pages.articlePurchase.studentPriceLabel' | translate }}
              </span>
            } @else {
              <span>{{ effectivePurchaseSubtotal() | currency: 'EUR' }}</span>
            }
          </div>
        </div>
        @if (effectivePurchaseVatAmount(); as vatAmount) {
          <div class="flex items-center justify-between gap-4">
            <span>
              {{
                'pages.articlePurchase.summaryVat'
                  | translate: { rate: purchasePrice()?.taxPercentage ?? 0 }
              }}
            </span>
            <span>{{ vatAmount | currency: 'EUR' }}</span>
          </div>
        }
        <div
          class="border-t border-secondary-border pt-4 text-base font-medium"
        >
          <div class="flex items-center justify-between gap-4">
            <span>{{ 'pages.articlePurchase.summaryTotal' | translate }}</span>
            <span>{{ effectivePurchaseTotal() | currency: 'EUR' }}</span>
          </div>
        </div>
      </div>

      <div class="-mx-8 flex flex-col gap-4 bg-primary-background px-8 py-4">
        <div class="flex flex-col gap-4">
          @if (useStudentPricing()) {
            <div>
              <label
                class="flex w-full items-center gap-3 text-left text-sm text-primary-text"
              >
                <input
                  class="checkbox"
                  formControlName="confirmStudentEnrollment"
                  type="checkbox"
                />
                <span class="flex-1 text-left leading-relaxed">
                  {{
                    'pages.articlePurchase.confirmStudentEnrollment' | translate
                  }}
                </span>
              </label>
              @if (
                confirmationFormGroup.controls.confirmStudentEnrollment
                  .invalid &&
                confirmationFormGroup.controls.confirmStudentEnrollment.touched
              ) {
                <p class="text-sm text-error-text">
                  {{ 'common.fieldValidation.required' | translate }}
                </p>
              }
            </div>
          }

          <div>
            <label
              class="flex w-full items-center gap-3 text-left text-sm text-primary-text"
            >
              <input
                class="checkbox"
                formControlName="acceptTerms"
                type="checkbox"
              />
              <span
                class="flex-1 text-left leading-relaxed"
                [innerHTML]="
                  'pages.articlePurchase.acceptTerms'
                    | translate: { termsRoute: termsRoute }
                "
              ></span>
            </label>
            @if (
              confirmationFormGroup.controls.acceptTerms.invalid &&
              confirmationFormGroup.controls.acceptTerms.touched
            ) {
              <p class="text-sm text-error-text">
                {{ 'common.fieldValidation.required' | translate }}
              </p>
            }
          </div>
        </div>

        @if (submitErrorKey()) {
          <div class="rounded-xl bg-error-light p-4 text-error-text">
            {{ submitErrorKey()! | translate }}
          </div>
        }

        <dhp-submit-button
          [disabled]="submitting()"
          [labelKey]="'pages.articlePurchase.submit'"
          [sendingLabelKey]="'pages.articlePurchase.submitting'"
          [sending]="submitting()"
        ></dhp-submit-button>
      </div>
    </aside>
  </form>
}
